import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { lastValueFrom, Subscription, interval } from 'rxjs';
import { UrlScanned } from 'src/app/models/bot.interface';
import { BotService } from 'src/app/core/services/bot.service';
import { GlobalObservableService } from 'src/app/core/services/global-observable.service';
import { takeWhile, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'globot-web-data-source',
  templateUrl: './web-data-source.component.html',
  styleUrls: ['./web-data-source.component.scss'],
})
export class WebDataSourceComponent implements OnInit, OnDestroy {
  @Output() backEvent = new EventEmitter<void>();
  @Output() urlDataSourceEvent = new EventEmitter<string>();
  @Output() nextEvent = new EventEmitter<void>();

  @Input() newBot: boolean = true;

  urlDatasource: string = '';
  showErrorMessage: boolean = false;
  errorMessage: string = '';

  isMobile = window.innerWidth < 768;
  loadingValue: number = 0;
  job_id: string | undefined;
  bot_id: string | undefined;
  links: UrlScanned[] = [];

  scanLoading: boolean = false;
  scanUrlReady: boolean = false;
  // Estado del job de escaneo: '' | PENDING | SCANNING | COMPLETE | ERROR. Maneja la
  // UI de progreso (spinner + contador en vivo mientras escanea, check al terminar).
  scanStatus: string = '';

  private subscription: Subscription = new Subscription();
  constructor(
    private globalService: GlobalObservableService,
    private readonly botService: BotService
  ) {}

  // Muestra el bloque de progreso mientras el scan está en curso o recién terminó.
  get showScanProgress(): boolean {
    return ['PENDING', 'SCANNING', 'COMPLETE'].includes(this.scanStatus);
  }

  get scanDone(): boolean {
    return this.scanStatus === 'COMPLETE';
  }

  ngOnInit() {
    this.scanUrlReady = environment.isScanUrlReady;
    this.subscription.add(
      this.globalService.getObservable().subscribe((message) => {
        if (message.type === 'jobId') {
          this.loadingValue = 10;
          let control = 3;
          this.job_id = message.data.jobId;
          this.bot_id = message.data.botId;
          this.scanStatus = 'PENDING';
          interval(1000)
            .pipe(
              // Pollear mientras el job NO esté en estado terminal. Antes cortaba al
              // llegar las primeras urls o al pasar a SCANNING, dejando el status
              // pegado en SCANNING y mostrando resultados parciales del primer nivel.
              takeWhile(
                () => this.scanStatus === 'PENDING' || this.scanStatus === 'SCANNING',
                true
              ),
              take(300)
            )
            .subscribe(
              async (count) => {
                if (count === control) {
                  control = control + 3;
                  // Seguir consultando aunque ya haya links: el scan es incremental
                  // y acumula urls nivel por nivel hasta COMPLETE. El contador en vivo
                  // (links.length) le da feedback al usuario; la barra es indeterminada.
                  this.scanStatus = (
                    await this.getScannedUrls(this.job_id!, this.bot_id!)
                  ).toUpperCase();
                }
              },
              (error) => {
                console.error('Error en el intervalo:', error);
              }
            );
        }
        if (message.type === 'jobId-error') {
          this.scanLoading = false;
          this.loadingValue = 0;
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.job_id != null) {
      this.delete_job(this.job_id);
    }
  }

  next(): void {
    this.nextEvent.emit();
  }

  back(): void {
    this.backEvent.emit();
  }

  backAndDeleteJob(): void {
    if (this.job_id != null) {
      this.delete_job(this.job_id);
    }
    this.backEvent.emit();
  }

  uploadUrls() {
    if (this.links.length == 0) {
      this.showErrorMessage = true;
      this.errorMessage = 'Debes cargar al menos una Url';
      return;
    }
    this.botService.uploadUrlsByJob(this.bot_id!, this.job_id!).subscribe({
      next: (_value) => {
        this.job_id = undefined;
        this.next();
      },
      error: (err) => {
        this.showErrorMessage = true;
        this.errorMessage =
          err?.status === 400 && err?.error?.code === 17
            ? 'Alcanzaste el límite de fuentes de tu plan. Elimina una existente si quieres agregar otra.'
            : 'No se pudo cargar la fuente de datos';
      },
    });
  }

  setUrlDataSource(): void {
    const urlRegex =
      /^(https:\/\/)?([a-z\d][a-z\d-]*[a-z\d]\.)+[a-z]{2,}(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=\-*]*)?(#[-a-z\d_]*)?$/i;
    if (this.urlDatasource.trim() === '') {
      this.showErrorMessage = true;
      this.errorMessage = 'El campo es requerido';
    } else if (!urlRegex.test(this.urlDatasource)) {
      this.showErrorMessage = true;
      this.errorMessage = 'Por favor, introduce una URL válida';
    } else {
      this.showErrorMessage = false;

      let normalizedUrl = this.normalizeUrl(this.urlDatasource);
      this.urlDataSourceEvent.emit(normalizedUrl);
      // this.next();
    }
  }

  normalizeUrl(url: string): string {
    let normalizedUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    return normalizedUrl.toLowerCase();
  }

  async getScannedUrls(jobId: string, botId: string): Promise<string> {
    try {
      const value = await lastValueFrom(this.botService.getScannedUrls(jobId, botId));
      this.links = value.chatbot_scanned_url;
      const status = value.status;
      const upper = status.toUpperCase();

      // El worker escribe 'ERROR' (no 'FAILED'); aceptamos ambos por compatibilidad.
      if (upper === 'ERROR' || upper === 'FAILED') {
        this.scanLoading = false;
        this.loadingValue = 100;
        this.showErrorMessage = true;
        this.errorMessage = 'No se pudo encontrar sitio web';
      }

      // Finalizar solo cuando el job terminó (COMPLETE), no apenas hay urls: con el
      // scan incremental las urls del primer nivel llegan mientras sigue SCANNING.
      if (upper === 'COMPLETE') {
        this.scanLoading = false;
        this.loadingValue = 100;
      }
      return status;
    } catch (error) {
      this.loadingValue = 0;
      this.errorMessage = 'No se pudo encontrar links asociados';
      this.showErrorMessage = true;
      this.scanLoading = false;
      return 'FAILED';
    }
  }

  obtainLinks() {
    this.scanLoading = true;
    const urlRegex =
      /^(https:\/\/)?([a-z\d][a-z\d-]*[a-z\d]\.)+[a-z]{2,}(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=\-*]*)?(#[-a-z\d_]*)?$/i;
    if (this.urlDatasource.trim() === '') {
      this.showErrorMessage = true;
      this.errorMessage = 'El campo es requerido';
      this.scanLoading = false;
      return;
    }

    if (!urlRegex.test(this.urlDatasource)) {
      this.showErrorMessage = true;
      this.errorMessage = 'Por favor, introduce una URL válida';
      this.scanLoading = false;
      return;
    }

    this.showErrorMessage = false;
    this.scanStatus = '';
    let normalizedUrl = this.normalizeUrl(this.urlDatasource);
    this.loadingValue = 5;
    if (this.job_id != null) {
      this.loadingValue = 0;
      this.delete_job(this.job_id);
      this.botService.scanUrl({ url: normalizedUrl }, this.bot_id!).subscribe({
        next: (value1) => {
          const jobId = value1['data']['id'];
          this.globalService.publish('jobId', { jobId: jobId, botId: this.bot_id! });
        },
      });
      return;
    }
    this.urlDataSourceEvent.emit(normalizedUrl);
  }

  delete_job(job_id: string) {
    this.links = [];
    this.scanStatus = '';
    this.loadingValue = 0;
    this.botService.deleteJobId(job_id).subscribe();
  }

  removeLink(link: string) {
    this.botService.deleteScannedUrl(this.job_id!, link).subscribe({
      next: (_value) => {
        this.links = this.links.filter((l) => l.id !== link);
      },
    });
  }
}
